import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { api } from "./api";
import { ErrorBanner, GhostButton, StepCard } from "./formParts";
import { Icon, T, cssVars, signupTheme, useSignupFonts } from "./signupDesign";
import PartnerHeader from "./PartnerHeader";

// Stripe draws this form in its own frame, so the page font is loaded into it.
const FONTS = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" }];

// Stripe's form in the sign-up form's look.
const APPEARANCE = {
  overlays: "dialog",
  variables: {
    fontFamily: T.body,
    fontSizeBase: "15px",
    colorPrimary: T.red,
    colorBackground: T.card,
    colorText: T.text,
    colorSecondaryText: T.hint,
    colorBorder: T.input,
    borderRadius: "12px",
  },
};

/**
 * The steps, before Stripe's form: Stripe makes the owner sign in first, so
 * say with which email (the Stripe account's, from the backend).
 */
function HowToConfirm({ email, several }) {
  const steps = [
    <>Click <strong>Add information</strong> below.</>,
    <>
      Stripe opens a small window to check it's you. Sign in with{" "}
      <strong>{email || "the email you signed the agreement with"}</strong> and enter the code Stripe sends you. No window? Allow pop-ups
      for this page, or use <strong>Continue on Stripe's site</strong> at the bottom.
    </>,
    <>Check the business, owner and bank details we filled in, and fix anything that's wrong.</>,
    <>On the last screen, accept Stripe's terms (<strong>Agree and submit</strong>).{several ? " The next restaurant then opens here." : ""}</>,
  ];
  return (
    <Box sx={{ background: T.infoBg, border: `1px solid ${T.infoBorder}`, borderRadius: "16px", px: { xs: 2, sm: 2.5 }, py: 2, mb: 2.5 }}>
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: 16, color: T.infoText, mb: 1 }}>How to confirm</Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, color: T.infoText, fontSize: 14.5, lineHeight: 1.6, "& li + li": { mt: 0.5 } }}>
        {steps.map((step, i) => <li key={i}>{step}</li>)}
      </Box>
    </Box>
  );
}

/**
 * The approved merchant's payout setup: /partner-signup/payouts?t=<token>,
 * from the "Confirm payout details" email (backend /merchant-onboarding/
 * stripe-setup redirects here). Stripe's own onboarding form, embedded: the
 * owner checks what the application filled in and agrees to Stripe's terms,
 * which only they can do. Stripe's hosted page is the fallback. An agreement
 * covering several restaurants has one link: each restaurant's own Stripe
 * account is set up in turn.
 */
export default function PayoutSetup() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("t");
  // loading | error | form | done
  const [state, setState] = useState({ phase: "loading" });
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(false);
  const holder = useRef(null);
  const session = useRef(null);
  useSignupFonts();

  // restaurantId: which of the agreement's restaurants; none, the first still to do.
  const load = useCallback((restaurantId) => {
    if (!token) {
      setState({ phase: "error", message: "This link is missing its code. Open it again from your email." });
      return () => {};
    }
    let alive = true;
    setError(null);
    api("POST", "/payout-setup/session", { body: { token, restaurantId } })
      .then((s) => {
        if (!alive) return;
        session.current = s;
        setState({
          phase: s.clientSecret ? "form" : "done",
          restaurantId: s.restaurantId,
          name: s.restaurantName,
          status: s.status,
          restaurants: s.restaurants || [],
        });
      })
      .catch((e) => alive && setState({ phase: e.status === 401 ? "error" : "failed", message: e.message }));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  // The form closed: where does this restaurant's account stand now? Done:
  // the next restaurant still to set up, if any.
  const check = useCallback(() => {
    const restaurantId = session.current && session.current.restaurantId;
    api("POST", "/payout-setup/status", { body: { token, restaurantId } })
      .then((status) => {
        if (!(status.stripeDetailsSubmitted && !status.due)) {
          setError("Stripe still needs a few details. Please finish the steps above.");
          return;
        }
        const others = ((session.current && session.current.restaurants) || []).filter((r) => r.restaurantId !== restaurantId && !r.done);
        if (others.length) load(others[0].restaurantId);
        else setState((s) => ({ ...s, phase: "done", status, restaurants: (s.restaurants || []).map((r) => ({ ...r, done: true })) }));
      })
      .catch((e) => setError(e.message));
  }, [token, load]);

  useEffect(() => {
    if (state.phase !== "form" || !holder.current || !session.current) return undefined;
    const restaurantId = session.current.restaurantId;
    const connect = loadConnectAndInitialize({
      publishableKey: session.current.publishableKey,
      // A session can be used once: the one the page loaded with goes to the
      // first ask, and every later ask (an expired session, the form mounted
      // again) gets a fresh one.
      fetchClientSecret: async () => {
        const first = session.current.clientSecret;
        if (first) {
          session.current = { ...session.current, clientSecret: null };
          return first;
        }
        const fresh = await api("POST", "/payout-setup/session", { body: { token, restaurantId } });
        return fresh.clientSecret;
      },
      fonts: FONTS,
      appearance: APPEARANCE,
    });
    const form = connect.create("account-onboarding");
    // Everything Stripe will ever ask for, now (as the hosted link does), so
    // payouts aren't paused later for a missing detail.
    form.setCollectionOptions({ fields: "eventually_due" });
    form.setOnExit(check);
    form.setOnLoadError(() => setError("Stripe's form didn't load. Use the button below to continue on Stripe's site."));
    holder.current.appendChild(form);
    return () => form.remove();
  }, [state.phase, state.restaurantId, token, check]);

  // Fallback: Stripe's own page, which comes back here when done.
  const openStripe = () => {
    setOpening(true);
    setError(null);
    api("POST", "/payout-setup/hosted", { body: { token, restaurantId: state.restaurantId } })
      .then(({ url }) => { window.location.href = url; })
      .catch((e) => {
        setOpening(false);
        setError(e.message);
      });
  };

  let body;
  if (state.phase === "loading") {
    body = <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress sx={{ color: T.red }} /></Box>;
  } else if (state.phase === "error" || state.phase === "failed") {
    body = (
      <StepCard title="Confirm payout details">
        <Alert severity="warning" icon={<Icon.Alert size={20} />}>{state.message}</Alert>
        {state.phase === "failed" && (
          <GhostButton onClick={() => { setState({ phase: "loading" }); load(); }} sx={{ mt: 2 }}>Try again</GhostButton>
        )}
      </StepCard>
    );
  } else if (state.phase === "done") {
    const live = state.status && state.status.stripeTransfersActive;
    const all = state.restaurants && state.restaurants.length > 1 ? state.restaurants.map((r) => r.name) : [state.name];
    const named = all.length > 1 ? `${all.slice(0, -1).join(", ")} and ${all[all.length - 1]}` : all[0];
    body = (
      <StepCard title={live ? "You're all set" : "Thank you, your details are in"}>
        <Alert severity="success" icon={<Icon.Check size={20} />} sx={{ mb: 2 }}>
          {live
            ? `Stripe has everything it needs. RushMyFood can now pay ${named} straight to your bank.`
            : "Stripe is checking your details. This usually takes a few minutes, sometimes a day or two. We'll start paying out as soon as Stripe confirms."}
        </Alert>
        <Typography sx={{ color: T.hint, fontSize: 14 }}>There's nothing else to do here. You can close this page.</Typography>
      </StepCard>
    );
  } else {
    const several = state.restaurants && state.restaurants.length > 1;
    body = (
      <StepCard
        title="Confirm payout details"
        subtitle={`For ${state.name}. We've filled in what you gave us in your application. Check it, then agree to Stripe's terms: Stripe is our payments partner, and we can only pay you once you've agreed.`}
      >
        {several && (
          <Box component="ol" aria-label="Your restaurants" sx={{ listStyle: "none", p: 0, m: 0, mb: 2.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {state.restaurants.map((r, i) => {
              const current = r.restaurantId === state.restaurantId;
              return (
                <Box
                  component="li"
                  key={r.restaurantId}
                  aria-current={current ? "step" : undefined}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.75, borderRadius: 999, fontSize: 14, fontWeight: 600,
                    border: `1px solid ${current ? T.red : T.cardBorder}`, background: r.done ? T.greenTint : current ? T.redTint : T.card,
                    color: r.done ? T.green : current ? T.redText : T.text2,
                  }}
                >
                  {r.done ? <Icon.Check size={16} /> : <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{i + 1}.</Box>}
                  {r.name}
                </Box>
              );
            })}
          </Box>
        )}
        <HowToConfirm email={state.status && state.status.email} several={several} />
        <ErrorBanner error={error} />
        <Box ref={holder} sx={{ minHeight: 320 }} />
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${T.cardBorder}`, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography sx={{ color: T.hint, fontSize: 14 }}>Having trouble with the form?</Typography>
          <GhostButton onClick={openStripe} disabled={opening} sx={{ gap: 1 }}>
            {opening ? <CircularProgress size={16} sx={{ color: T.red }} /> : <Icon.External size={16} />}
            Continue on Stripe's site
          </GhostButton>
        </Box>
      </StepCard>
    );
  }

  return (
    <Box sx={{ background: T.pageBg, minHeight: "100vh" }}>
      <ThemeProvider theme={signupTheme}>
        <PartnerHeader />
        <Box sx={{ ...cssVars, fontFamily: T.body, color: T.text, maxWidth: 760, mx: "auto", px: 2, pt: { xs: "88px", md: "112px" }, pb: 6 }}>
          {body}
        </Box>
      </ThemeProvider>
    </Box>
  );
}
